import { lazy, Suspense, useCallback, useEffect, useMemo, useState, useTransition } from "react";
import { createRoot } from "react-dom/client";
import {
  ExternalLink,
  Loader2,
  RefreshCw,
  Settings,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import {
  AuthMePayload,
  BillingEntry,
  BillingPayment,
  BillingSmsUsage,
  BillingSummary,
  OrderDetailPayload,
  OrderSummary,
  OrdersPayload,
  SettingsPayload,
  completeTelegramLogin,
  currentTenantCode,
  loadOrderDetail,
  loadAuthMe,
  loadBillingLedger,
  loadBillingPayments,
  loadBillingSms,
  loadBillingSummary,
  loadOrders,
  loadSettings,
  loadTelegramLoginOptions,
  logoutAuth,
  setActiveTenantCode,
} from "./api";
import "./styles.css";
import { WorkspaceLoadState } from "./WorkspaceLoadState";
import { useUnsavedChangesWarning } from "./useUnsavedChangesWarning";
import { launch, useDeepLink } from "./useDeepLink";

const BillingScreen = lazy(() => import("./BillingScreen").then((module) => ({ default: module.BillingScreen })));
const HomeScreen = lazy(() => import("./OrdersScreens").then((module) => ({ default: module.HomeScreen })));
const OrdersScreen = lazy(() => import("./OrdersScreens").then((module) => ({ default: module.OrdersScreen })));
const CreateOrderScreen = lazy(() => import("./CreateOrderScreen").then((module) => ({ default: module.CreateOrderScreen })));
const OrderDetailScreen = lazy(() => import("./OrderDetailScreen").then((module) => ({ default: module.OrderDetailScreen })));
const LegalDocumentsScreen = lazy(() => import("./LegalDocumentsScreen").then((module) => ({ default: module.LegalDocumentsScreen })));

type View = "home" | "create" | "orders" | "order" | "billing" | "legal";
type LegalTab = "agreement" | "privacy";

function publicLegalTab(): LegalTab | null {
  const route = window.location.hash.replace(/^#\/?/, "").toLowerCase();
  if (route === "legal/agreement") return "agreement";
  if (route === "legal/privacy") return "privacy";
  return null;
}

type OrderCopyDraft = {
  retry?: import("./api").CreationRetry;
  phone: string;
  recipientName: string;
  cityName: string;
  cityCode: number;
  countryCode: string;
  pvzCode: string;
  pvzAddress: string;
  adultGoods: boolean;
};

const ORDER_COPY_DRAFT_KEY = "cdek-miniapp-v2-copy-order-draft";

function orderCopyDraft(order?: OrderDetailPayload["order"] | null): OrderCopyDraft | null {
  if (!order || order.is_return_waybill) return null;
  const phone = text(order.recipient_phone || order.phone, "");
  const recipientName = text(order.recipient_name, "");
  if (!phone || !recipientName) return null;
  return {
    phone,
    recipientName,
    cityName: text(order.city_name, ""),
    cityCode: Number(order.city_code || 0),
    countryCode: text(order.country_code, "RU"),
    pvzCode: text(order.pvz_code, ""),
    pvzAddress: text(order.pvz_address, ""),
    adultGoods: Boolean(order.adult_goods),
    retry: order.creation_state === "rejected" ? order.creation_retry : undefined,
  };
}

function text(value: unknown, fallback = "-"): string {
  const result = String(value ?? "").trim();
  return result || fallback;
}

function orderInternalNumber(order?: Partial<OrderSummary> | null): string {
  if (!order) return "";
  return text(order.internal_order_number || order.cdek_number, "");
}

function useTelegramChrome(view: View, navigate: (view: View) => void) {
  useEffect(() => {
    const webApp = window.Telegram?.WebApp;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const applyTheme = () => {
      document.documentElement.dataset.theme = webApp?.colorScheme || (media.matches ? "dark" : "light");
    };
    applyTheme();
    webApp?.ready?.();
    webApp?.expand?.();
    webApp?.onEvent?.("themeChanged", applyTheme);
    media.addEventListener?.("change", applyTheme);
    return () => {
      webApp?.offEvent?.("themeChanged", applyTheme);
      media.removeEventListener?.("change", applyTheme);
    };
  }, []);

  useEffect(() => {
    const back = window.Telegram?.WebApp?.BackButton;
    if (!back) return;
    const onBack = () => navigate(view === "order" ? "orders" : "home");
    if (view !== "home") {
      back.show?.();
      back.onClick?.(onBack);
    } else {
      back.hide?.();
    }
    return () => back.offClick?.(onBack);
  }, [navigate, view]);
}

function LoginGate({ auth, error, loading, onLogin }: {
  auth: AuthMePayload | null;
  error: string;
  loading: boolean;
  onLogin: () => void;
}) {
  const loginEnabled = Boolean(auth?.login?.telegram_login_enabled);
  const checkingAccess = loading && !auth;
  return (
    <main className="access-screen">
      <section className="access-card">
        <div className="auth-brand">
          <span className="scl-logo-window scl-logo-large">
            <img src="/static/branding/scl-logo.png" alt="SCL by DR.Mult" />
          </span>
          <span>Simple Cabinet Logistics</span>
        </div>
        <h1>Вход в SCL</h1>
        <p>{error || auth?.detail || (checkingAccess ? "Проверяем доступ..." : "Войдите через Telegram, чтобы открыть клиентский интерфейс.")}</p>
        {loginEnabled ? (
          <button className="primary" type="button" onClick={onLogin} disabled={loading}>
            {loading ? <Loader2 className="spin" size={18} /> : <ShieldCheck size={18} />}
            Войти через Telegram
          </button>
        ) : checkingAccess ? (
          <div className="notice">Одну секунду, проверяем вашу сессию.</div>
        ) : (
          <div className="notice">Откройте MiniApp из Telegram или настройте Telegram Login.</div>
        )}
      </section>
    </main>
  );
}

function App() {
  const publicLegal = publicLegalTab();
  const [auth, setAuth] = useState<AuthMePayload | null>(null);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [billingEntries, setBillingEntries] = useState<BillingEntry[]>([]);
  const [billingPayments, setBillingPayments] = useState<BillingPayment[]>([]);
  const [billingSms, setBillingSms] = useState<BillingSmsUsage[]>([]);
  const [orders, setOrders] = useState<OrdersPayload | null>(null);
  const [view, setView] = useState<View>(() => (
    new URLSearchParams(window.location.search).get("copy_order") === "1" ? "create" : "home"
  ));
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetailPayload["order"] | null>(null);
  const [error, setError] = useState("");
  const [linkError, setLinkError] = useState(launch.error);
  const [loading, setLoading] = useState(true);
  const [isPending, startTransition] = useTransition();
  const [createDirty, setCreateDirty] = useState(false);
  const [createFormVersion, setCreateFormVersion] = useState(0);
  const [activeTenant, setActiveTenant] = useState(() => currentTenantCode());
  const [legalTab, setLegalTab] = useState<LegalTab>("agreement");
  const [copyDraft, setCopyDraft] = useState<OrderCopyDraft | null>(() => {
    try {
      const raw = window.localStorage.getItem(ORDER_COPY_DRAFT_KEY);
      window.localStorage.removeItem(ORDER_COPY_DRAFT_KEY);
      return raw ? JSON.parse(raw) as OrderCopyDraft : null;
    } catch {
      window.localStorage.removeItem(ORDER_COPY_DRAFT_KEY);
      return null;
    }
  });

  const navigate = useCallback((nextView: View) => {
    if (view === "create" && nextView !== "create" && createDirty) {
      if (!window.confirm("Данные нового заказа не сохранены. Покинуть форму?")) return;
    }
    setView(nextView);
  }, [createDirty, view]);

  useTelegramChrome(view, navigate);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [view]);

  useEffect(() => {
    if (billing?.read_only && view === "create") {
      setCreateDirty(false);
      setView("home");
    }
  }, [billing?.read_only, view]);

  useUnsavedChangesWarning(createDirty);

  const authenticated = Boolean(auth?.authenticated || window.Telegram?.WebApp?.initData);
  const busy = loading || isPending;
  const workspaceReady = Boolean(settings && orders && billing);
  useDeepLink({ ready: workspaceReady && !loading, tenant: settings?.tenant_code,
    isAdmin: settings?.is_admin, openOrder: openOrderValue, navigate: setView, error: setLinkError });

  async function refresh(nextQuery = query, nextFilter = filter, nextPage = ordersPage, nextMineOnly = mineOnly) {
    let requestedTenant = currentTenantCode();
    setLoading(true);
    setError("");
    try {
      let authPayload: AuthMePayload;
      try {
        authPayload = await loadAuthMe();
      } catch (exc) {
        const apiError = exc as Error & { status?: number };
        if (apiError.status !== 403 || !currentTenantCode() || launch.value) throw exc;
        setActiveTenantCode("");
        setActiveTenant("");
        requestedTenant = "";
        authPayload = await loadAuthMe();
      }
      setAuth(authPayload);
      if (!authPayload.authenticated && !window.Telegram?.WebApp?.initData) return false;
      const [settingsPayload, ordersPayload, billingPayload, ledgerPayload, paymentsPayload, smsPayload] = await Promise.all([
        loadSettings(),
        loadOrders({ q: nextQuery, filter: nextFilter, page: nextPage, limit: 12, mine: nextMineOnly }),
        loadBillingSummary(),
        loadBillingLedger(),
        loadBillingPayments(),
        loadBillingSms(),
      ]);
      if (requestedTenant !== currentTenantCode()) return false;
      setSettings(settingsPayload);
      const resolvedTenant = text(settingsPayload.tenant_code, "").toLowerCase();
      if (resolvedTenant && resolvedTenant !== currentTenantCode()) {
        setActiveTenantCode(resolvedTenant);
        setActiveTenant(resolvedTenant);
      }
      setOrders(ordersPayload);
      setBilling(billingPayload);
      setBillingEntries(ledgerPayload.items || []);
      setBillingPayments(paymentsPayload.items || []);
      setBillingSms(smsPayload.items || []);
      return true;
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : "Не удалось загрузить MiniApp");
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function handleGlobalRefresh() {
    if (view === "create") {
      setCreateDirty(false);
      setCopyDraft(null);
      setCreateFormVersion((current) => current + 1);
    }
    await refresh();
  }

  async function switchTenant(nextTenant: string) {
    const normalized = nextTenant.trim().toLowerCase();
    if (!normalized || normalized === activeTenant) return;
    if (createDirty && !window.confirm("Данные нового заказа не сохранены. Переключить клиента?")) return;
    const previous = activeTenant;
    setLoading(true);
    setError("");
    setActiveTenantCode(normalized);
    setActiveTenant(normalized);
    setCreateDirty(false);
    setCopyDraft(null);
    setCreateFormVersion((current) => current + 1);
    setSelectedOrder(null);
    setOrders(null);
    setSettings(null);
    setBilling(null);
    setBillingEntries([]);
    setBillingPayments([]);
    setBillingSms([]);
    setQuery("");
    setFilter("all");
    setMineOnly(false);
    setOrdersPage(1);
    setView("home");
    const switched = await refresh("", "all", 1, false);
    if (!switched) {
      setActiveTenantCode(previous);
      setActiveTenant(previous);
    }
  }

  async function openOrder(order: OrderSummary) {
    const orderValue = orderInternalNumber(order);
    await openOrderValue(orderValue);
  }

  async function openOrderValue(orderValue: string) {
    if (!orderValue) return;
    setView("order");
    setLoading(true);
    setError("");
    setSelectedOrder(null);
    const requestedTenant = currentTenantCode();
    try {
      const payload = await loadOrderDetail(orderValue);
      if (requestedTenant !== currentTenantCode()) return;
      setSelectedOrder(payload.order || null);
    } catch (exc) {
      if (requestedTenant !== currentTenantCode()) return;
      setError(exc instanceof Error ? exc.message : "Не удалось открыть заказ");
    } finally {
      if (requestedTenant === currentTenantCode()) setLoading(false);
    }
  }

  function copyOrder(order: OrderDetailPayload["order"]) {
    const draft = orderCopyDraft(order);
    if (!draft) {
      setError("В заказе недостаточно данных получателя для копирования.");
      return;
    }
    setError("");
    setCreateDirty(false);
    setCopyDraft(draft);
    setCreateFormVersion((current) => current + 1);
    setSelectedOrder(null);
    setView("create");
  }

  async function loginWithTelegram() {
    setLoading(true);
    setError("");
    try {
      const options = await loadTelegramLoginOptions();
      await new Promise<void>((resolve, reject) => {
        if (!window.Telegram?.Login?.auth) {
          reject(new Error("Telegram Login Widget недоступен"));
          return;
        }
        window.Telegram.Login.auth(
          {
            client_id: options.client_id,
            scope: options.scope || ["openid", "profile"],
            nonce: options.nonce,
            lang: "ru",
          },
          (result) => {
            if (result.id_token) {
              completeTelegramLogin(result.id_token).then((payload) => {
                setAuth(payload);
                resolve();
              }).catch(reject);
              return;
            }
            reject(new Error(result.error || "Telegram Login не вернул id_token"));
          },
        );
      });
      await refresh();
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : "Не удалось войти через Telegram");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!publicLegal) refresh();
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    const timeout = window.setTimeout(() => {
      setOrdersPage(1);
      startTransition(() => {
        refresh(query, filter, 1, mineOnly);
      });
    }, 320);
    return () => window.clearTimeout(timeout);
  }, [query, filter, mineOnly]);

  function changeOrdersPage(page: number) {
    setOrdersPage(page);
    startTransition(() => {
      refresh(query, filter, page, mineOnly);
    });
  }

  const shellSubtitle = useMemo(() => {
    const tenant = text(settings?.tenant_code || auth?.tenant?.["tenant_code"], "");
    const version = text(settings?.app_version, "");
    return ["SCL", tenant, version].filter(Boolean).join(" · ");
  }, [auth?.tenant, settings]);

  if (publicLegal) {
    return (
      <main className="miniapp-v2 public-legal-v2">
        <Suspense fallback={<section className="route-loading" role="status"><Loader2 className="spin" size={22}/><span>Открываем документ…</span></section>}>
          <LegalDocumentsScreen initialTab={publicLegal} />
        </Suspense>
        <footer className="app-footer">
          <div className="developer-credit" aria-label="SCL by DR.Mult">
            <span className="scl-logo-window"><img src="/static/branding/scl-logo.png" alt="SCL by DR.Mult" /></span>
            <span>Простой Кабинет Логистики</span>
          </div>
        </footer>
      </main>
    );
  }

  if (!authenticated) {
    return <LoginGate auth={auth} error={error} loading={loading} onLogin={loginWithTelegram} />;
  }

  return (
    <main className="miniapp-v2" aria-busy={busy}>
      <header className="app-header">
        <div className="brand">
          <div className="brand-mark">S</div>
          <div>
            <strong>{text(settings?.client_name, "SCL")}</strong>
            <span>{shellSubtitle}</span>
          </div>
        </div>
        <div className="header-actions">
          {(settings?.tenants?.length || 0) > 1 ? (
            <label className="tenant-switch-v2">
              <span>Клиент</span>
              <select value={activeTenant || settings?.tenant_code || ""} onChange={(event) => switchTenant(event.target.value)} disabled={busy}>
                {(settings?.tenants || []).map((tenant) => {
                  const code = text(tenant.tenant_code, "").toLowerCase();
                  const inactive = tenant.is_active === false ? " · выключен" : "";
                  return <option key={code} value={code}>{tenant.name ? `${tenant.name} (${code})${inactive}` : `${code}${inactive}`}</option>;
                })}
              </select>
            </label>
          ) : null}
          {billing?.billing_enabled ? (
            <button className={`balance-button-v2 ${billing.balance_rub <= billing.low_balance_threshold_rub ? "low" : ""}`} type="button" onClick={() => navigate("billing")}>
              <WalletCards size={17}/><span>{Number(billing.balance_rub || 0).toLocaleString("ru-RU")} ₽</span>
            </button>
          ) : null}
          {settings?.is_admin ? (
            <a className="icon-link" href={settings?.admin_ui?.canonical_path || settings?.admin_ui?.default_path || "/admin"} title="Админка" aria-label="Админка">
              <Settings size={18} />
            </a>
          ) : null}
          <button
            className="icon-button"
            type="button"
            onClick={() => handleGlobalRefresh()}
            disabled={busy}
            title={view === "create" ? "Очистить форму и обновить" : "Обновить"}
            aria-label={view === "create" ? "Очистить форму и обновить" : "Обновить"}
          >
            {busy ? <Loader2 className="spin" size={18} /> : <RefreshCw size={18} />}
          </button>
        </div>
      </header>

      {error || linkError ? <section className="notice notice-bad" role="alert">{error || linkError}</section> : null}

      <nav className="tabbar" aria-label="Разделы MiniApp">
        <button className={view === "home" ? "active" : ""} aria-current={view === "home" ? "page" : undefined} type="button" onClick={() => navigate("home")}>Главная</button>
        <button className={view === "create" ? "active" : ""} aria-current={view === "create" ? "page" : undefined} type="button" disabled={!workspaceReady || Boolean(billing?.read_only)} title={billing?.read_only ? "Недостаточно средств" : "Создать заказ"} onClick={() => navigate("create")}>Создать</button>
        <button className={view === "orders" || view === "order" ? "active" : ""} aria-current={view === "orders" || view === "order" ? "page" : undefined} type="button" onClick={() => navigate("orders")}>Заказы</button>
      </nav>

      <Suspense fallback={<section className="route-loading" role="status" aria-live="polite"><Loader2 className="spin" size={22}/><span>Открываем раздел…</span></section>}>
      {!workspaceReady ? <WorkspaceLoadState busy={busy} onRetry={() => { void refresh(); }} /> : view === "home" ? (
        <HomeScreen
          settings={settings}
          orders={orders}
          onOpenOrders={(nextFilter = "all") => { setQuery(""); setFilter(nextFilter); setOrdersPage(1); navigate("orders"); }}
          onCreateOrder={() => navigate("create")} onOpenOrder={openOrder}
          createDisabled={Boolean(billing?.read_only)}
        />
      ) : view === "billing" && billing ? (
        <BillingScreen summary={billing} entries={billingEntries} payments={billingPayments} sms={billingSms} onPaymentCreated={async () => { await refresh(query, filter, ordersPage, mineOnly); }} onOpenOrder={openOrderValue} />
      ) : view === "legal" && settings?.show_legal_documents ? (
        <LegalDocumentsScreen key={legalTab} initialTab={legalTab} />
      ) : view === "create" ? (
        <CreateOrderScreen
          key={`create-order-${createFormVersion}`}
          settings={settings}
          copyDraft={copyDraft}
          onCreated={openOrderValue}
          onDirtyChange={setCreateDirty}
        />
      ) : view === "orders" ? (
        <OrdersScreen
          orders={orders}
          query={query}
          filter={filter}
          mineOnly={mineOnly}
          loading={busy}
          onQuery={setQuery}
          onFilter={setFilter}
          onMineOnly={setMineOnly}
          onRefresh={() => refresh(query, filter, ordersPage, mineOnly)}
          onOpenOrder={openOrder}
          onPage={changeOrdersPage}
        />
      ) : (
        <OrderDetailScreen
          order={selectedOrder}
          settings={settings}
          loading={busy}
          readOnly={Boolean(billing?.read_only)}
          onUpdated={(nextOrder) => setSelectedOrder(nextOrder || null)}
          onOpenOrder={openOrderValue}
          onCopyOrder={copyOrder}
          onDeleted={() => {
            setSelectedOrder(null);
            setView("orders");
            refresh(query, filter, ordersPage, mineOnly);
          }}
          onBack={() => {
            setSelectedOrder(null);
            setView("orders");
          }}
        />
      )}
      </Suspense>

      <footer className="app-footer">
        <span>Создание заказов, документы и отслеживание.</span>
        {settings?.show_legal_documents ? <nav className="legal-footer-links-v2" aria-label="Документы сервиса">
          <button type="button" onClick={() => { setLegalTab("agreement"); navigate("legal"); }}>Пользовательское соглашение</button>
          <button type="button" onClick={() => { setLegalTab("privacy"); navigate("legal"); }}>Политика конфиденциальности</button>
        </nav> : null}
        <div className="developer-credit" aria-label="SCL by DR.Mult">
          <span className="scl-logo-window">
            <img src="/static/branding/scl-logo.png" alt="SCL by DR.Mult" />
          </span>
          <span>Простой Кабинет Логистики</span>
        </div>
        {auth?.authenticated ? (
          <button type="button" onClick={() => logoutAuth().then(() => window.location.reload())}>
            Выйти <ExternalLink size={14} />
          </button>
        ) : null}
      </footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
